import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { graphql } from 'react-apollo';
import { withRouter } from 'react-router-dom';
import compose from 'lodash.flowright';
import { connect } from 'react-redux';

import Button from '../Button';
import {
  AlbumForContributorShare,
  AlbumFolderForContributorShare,
} from '../../../graphql/queries/contributors';
import useUploadPermission from '../../../apps/pro/services/contributors/hooks/useUploadPermission';
import { openModal as openModalAction } from '../../../actions/modal-actions';
import { PHONE_AUTH_MODAL } from '../../../constants/modals';
import { MeQuery } from '../../../graphql/queries/account';
import { USERNAME_SELECT_MODAL } from '../../../apps/web-gallery/components/UsernameSelect/modal';
import ACCOUNT_ROLES from '../../../constants/account-roles';
import { accountAddRoles } from '../../../graphql/mutations/account';
import { isProAccount } from '../../../util/account';
import { REDIRECT_MODE } from '../../../apps/pro/views/AutoContributorLinkHandler/helpers';
import useVideoUploadPermission from '../../../apps/pro/services/contributors/hooks/useVideoUploadPermission';
import { canContributeToEntity } from './helpers';

const propTypes = {
  album: PropTypes.shape({
    id: PropTypes.string,
    auto_contributor_share_url: PropTypes.string,
  }),
  albumFolder: PropTypes.shape({
    id: PropTypes.string,
    auto_contributor_share_url: PropTypes.string,
  }),
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
  fallbackContent: PropTypes.oneOf([PropTypes.node, PropTypes.func]),
  history: PropTypes.shape({
    push: PropTypes.func,
  }),
  authenticated: PropTypes.bool,
  openModal: PropTypes.func,
  user: PropTypes.shape({
    id: PropTypes.string,
    username: PropTypes.string,
  }),
  addShutterbugRole: PropTypes.func,
};

const ContributeButton = ({
  album,
  albumFolder,
  children = 'Contribute',
  fallbackContent = null,
  history,
  authenticated,
  openModal,
  user,
  addShutterbugRole,
  ...props
}) => {
  const [authenticatedByButton, setAuthenticatedByButton] = useState(false);
  const entity = album || albumFolder;
  const autoContributorShareUrl = entity?.auto_contributor_share_url;
  const autoContributorShareUrlUpload = `${autoContributorShareUrl}?mode=${REDIRECT_MODE.UPLOAD}`;
  const isRenderProps = typeof children === 'function';

  useEffect(() => {
    if (authenticatedByButton && user) {
      if (user.username) {
        // redirect
        window.location.assign(autoContributorShareUrlUpload);
      } else {
        // If no username is found, fill that in.
        openModal(USERNAME_SELECT_MODAL, {
          onUsernameSelected: () => {
            window.location.assign(autoContributorShareUrlUpload);
          },
        });
      }
    }
  }, [authenticatedByButton, user?.id]);

  const { isAllowed } = useUploadPermission({
    entityId: entity?.id,
  });

  const { isAllowed: isAllowedToUploadVideo } = useVideoUploadPermission({
    entityId: entity?.id,
    // Important to check for ownership here. Otherwise, we'll assume the user is
    // the owner when it isn't a contributor
    checkOwnership: true,
  });

  // Covers video uploading case specifically.
  if (!entity) {
    return null;
  }

  if (!canContributeToEntity(entity, isAllowed, isAllowedToUploadVideo)) {
    return typeof fallbackContent === 'function'
      ? fallbackContent()
      : fallbackContent;
  }

  // Otherwise, one of the following is true:
  // - The entity allows auto-contribute setting and, as such, users can give themselves permission to upload.
  // - The user is allowed to upload already.
  if (isAllowed && album && authenticated) {
    const albumUrl = album.is_video_album
      ? `/app/shutterbug/albums/${album.id}/record-video`
      : `/contributors/upload/${album?.id}`;

    return isRenderProps ? (
      children({ onClick: () => history.push(albumUrl) })
    ) : (
      <Button to={albumUrl} {...props}>
        {children}
      </Button>
    );
  }

  if (isAllowed && albumFolder && authenticated) {
    const albumFolderUrl = `/contributors/upload/${albumFolder?.id}`;

    return isRenderProps ? (
      children({ onClick: () => history.push(albumFolderUrl) })
    ) : (
      <Button to={albumFolderUrl} {...props}>
        {children}
      </Button>
    );
  }

  const onAutoContribute = async () => {
    if (!authenticated) {
      openModal(PHONE_AUTH_MODAL, {
        onLogin: () => {
          setAuthenticatedByButton(true);
        },
      });

      return;
    }

    if (authenticated && !user.username) {
      openModal(USERNAME_SELECT_MODAL, {
        onUsernameSelected: () => {
          window.location.assign(autoContributorShareUrlUpload);
        },
      });

      return;
    }

    const shouldAddShutterbugRole = !isProAccount(user);

    if (
      shouldAddShutterbugRole &&
      !(user.roles || []).includes(ACCOUNT_ROLES.SHUTTERBUG)
    ) {
      await addShutterbugRole(user.id);
    }

    window.location.assign(autoContributorShareUrlUpload);
  };

  return isRenderProps ? (
    children({ onClick: onAutoContribute })
  ) : (
    <Button onClick={onAutoContribute} {...props}>
      {children}
    </Button>
  );
};

export { ContributeButton as ContributeButtonComponent };

const withAlbumForContributorShare = graphql(AlbumForContributorShare, {
  options: ({ match, albumId }) => ({
    variables: {
      id: albumId || match.params.albumId,
    },
  }),
  props: ({ data: { album, loading } = {} }) => ({
    album,
    loading,
  }),
  skip: ({ albumId, match }) => !albumId && !match.params.albumId,
});

const withAlbumFolderForContributorShare = graphql(
  AlbumFolderForContributorShare,
  {
    options: ({ match, albumFolderId }) => ({
      variables: {
        id:
          albumFolderId || match.params.folderId || match.params.albumFolderId,
      },
    }),
    props: ({ data: { albumFolder, loading } = {} }) => ({
      albumFolder,
      loading,
    }),
    skip: ({ match, albumFolderId }) =>
      !albumFolderId && !match.params.folderId && !match.params.albumFolderId,
  },
);

const withUserData = graphql(MeQuery, {
  props: ({ data }) => ({
    user: data?.me,
  }),
  skip: ({ authenticated }) => !authenticated,
});

const withAccountAddRolesMutation = graphql(accountAddRoles, {
  props: ({ mutate }) => ({
    addShutterbugRole: accountId =>
      mutate({
        variables: {
          input: {
            account_id: accountId,
            // note: ACCOUNT_ROLES enum contains lowercase, but API requires uppercase
            roles: [ACCOUNT_ROLES.SHUTTERBUG.toUpperCase()],
          },
        },
      }),
  }),
});

const mapStateToProps = ({ auth }) => ({
  authenticated: auth.authenticated,
});

const mapDispatchToProps = {
  openModal: openModalAction,
};

ContributeButton.propTypes = propTypes;

export default compose(
  withRouter,
  connect(mapStateToProps, mapDispatchToProps),
  withAlbumForContributorShare,
  withAlbumFolderForContributorShare,
  withUserData,
  withAccountAddRolesMutation,
)(ContributeButton);
